import {useEffect, useRef} from "react";

// Keep focus in the active panel and return it to the row/button that opened it.
// The close callback may change while another linked operation is loading.
export function useFinanceDialog(onClose: () => void): React.RefObject<HTMLElement> {
    const panel = useRef<HTMLElement>(null);
    const close = useRef(onClose);
    close.current = onClose;
    useEffect(() => {
        const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const overflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const focusables = (): HTMLElement[] => Array.from(panel.current?.querySelectorAll<HTMLElement>(
            'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), summary, [tabindex="0"]',
        ) || []).filter(element => {
            if (element.closest('[hidden]')) return false;
            for (let parent = element.parentElement; parent; parent = parent.parentElement) {
                if (parent instanceof HTMLDetailsElement && !parent.open) {
                    const summary = Array.from(parent.children).find(child => child.tagName === 'SUMMARY');
                    if (!summary?.contains(element)) return false;
                }
            }
            return true;
        });
        focusables()[0]?.focus();
        const keydown = (event: KeyboardEvent): void => {
            if (event.key === "Escape") { event.preventDefault(); close.current(); return; }
            if (event.key !== "Tab") return;
            const elements = focusables();
            const first = elements[0];
            const last = elements[elements.length - 1];
            if (!first) { event.preventDefault(); return; }
            if (event.shiftKey && (document.activeElement === first || !panel.current?.contains(document.activeElement))) {
                event.preventDefault(); last.focus();
            } else if (!event.shiftKey && (document.activeElement === last || !panel.current?.contains(document.activeElement))) {
                event.preventDefault(); first.focus();
            }
        };
        window.addEventListener("keydown", keydown);
        return () => {
            window.removeEventListener("keydown", keydown);
            document.body.style.overflow = overflow;
            if (previous?.isConnected) previous.focus();
        };
    }, []);
    return panel;
}
